import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Users, Search, Plus, AlertCircle, RefreshCw } from 'lucide-react';
import { getDistributors } from '../../api/wholesale/distributors';
import AddDistributorModal from '../../components/wholesale/AddDistributorModal';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const DistributorsPage = () => {
  const [distributors, setDistributors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const fetchDistributors = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getDistributors({ search: searchQuery || undefined });
      const data = response?.data || response || [];
      setDistributors(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching distributors:', err);
      setError('Failed to load distributors');
      setDistributors([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDistributors();
  }, []);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchDistributors();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const tierColors = {
    bronze: 'bg-amber-100 text-amber-700',
    silver: 'bg-gray-100 text-gray-700',
    gold: 'bg-yellow-100 text-yellow-700',
    platinum: 'bg-purple-100 text-purple-700',
    Bronze: 'bg-amber-100 text-amber-700',
    Silver: 'bg-gray-100 text-gray-700',
    Gold: 'bg-yellow-100 text-yellow-700',
    Platinum: 'bg-purple-100 text-purple-700'
  };

  const statusColors = {
    active: 'bg-emerald-100 text-emerald-700',
    suspended: 'bg-red-100 text-red-700',
    inactive: 'bg-gray-100 text-gray-700'
  };

  const filteredDistributors = distributors.filter((d) => {
    // Status filter
    if (statusFilter !== 'all' && d.customer_status !== statusFilter) {
      return false;
    }
    return true;
  });

  // Pagination
  const totalItems = filteredDistributors.length;
  const totalPages = Math.ceil(totalItems / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedDistributors = filteredDistributors.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter]);

  if (loading && distributors.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Customers</h1>
          <p className="text-gray-500 mt-1">Manage your wholesale customers and associates</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchDistributors}
            className="inline-flex items-center gap-2 px-3 py-2 text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => setIsAddModalOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Add Customer
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search distributors..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-gray-200 rounded-lg bg-white"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {/* Empty State */}
      {!loading && filteredDistributors.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Users className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No customers found</h3>
          <p className="text-gray-500 mb-4">
            {searchQuery
              ? 'Try a different search term'
              : 'Get started by adding your first customer'}
          </p>
          <button
            onClick={() => setIsAddModalOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
          >
            <Plus className="w-5 h-5" />
            Add Customer
          </button>
        </div>
      )}

      {/* Customers Table */}
      {filteredDistributors.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-gray-900">
                    Customer
                  </th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-gray-900">
                    Contact
                  </th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-gray-900">
                    Status
                  </th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-gray-900">
                    Price Tier
                  </th>
                  <th className="text-right px-6 py-4 text-sm font-semibold text-gray-900">
                    <div className="flex items-center justify-end gap-1">
                      <span>Outstanding Balance</span>
                      <div className="group relative">
                        <AlertCircle className="w-3 h-3 text-gray-400 cursor-help" />
                        <div className="absolute right-0 bottom-full mb-2 hidden group-hover:block w-48 p-2 bg-gray-900 text-white text-xs rounded shadow-lg z-10">
                          Total unpaid order amounts
                        </div>
                      </div>
                    </div>
                  </th>
                  <th className="text-right px-6 py-4 text-sm font-semibold text-gray-900">
                    <div className="flex items-center justify-end gap-1">
                      <span>Balance</span>
                      <div className="group relative">
                        <AlertCircle className="w-3 h-3 text-gray-400 cursor-help" />
                        <div className="absolute right-0 bottom-full mb-2 hidden group-hover:block w-48 p-2 bg-gray-900 text-white text-xs rounded shadow-lg z-10">
                          Net balance (Account - Outstanding)
                        </div>
                      </div>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {paginatedDistributors.map((distributor) => {
                  const outstandingBalance = parseFloat(distributor.outstanding_balance) || 0;
                  const accountBalance = parseFloat(distributor.account_balance) || 0;
                  const netBalance = accountBalance - outstandingBalance;
                  const tierName = distributor.tier_name || distributor.price_tier?.name || '-';
                  const status = distributor.customer_status || 'active';
                  return (
                    <tr
                      key={distributor.id}
                      className="hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() =>
                        (window.location.href = `/wholesale/distributors/${distributor.id}`)
                      }
                    >
                      <td className="px-6 py-4">
                        <p className="font-medium text-gray-900">{distributor.business_name}</p>
                        <p className="text-sm text-gray-500">{distributor.contact_person_name}</p>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-gray-900">{distributor.phone_number}</p>
                        <p className="text-sm text-gray-500">{distributor.email}</p>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${statusColors[status] || 'bg-gray-100 text-gray-700'}`}
                        >
                          {status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                            tierName === 'Bronze'
                              ? 'bg-amber-100 text-amber-700'
                              : tierName === 'Silver'
                                ? 'bg-gray-100 text-gray-700'
                                : tierName === 'Gold'
                                  ? 'bg-yellow-100 text-yellow-700'
                                  : tierName === 'Platinum'
                                    ? 'bg-purple-100 text-purple-700'
                                    : 'bg-gray-100 text-gray-500'
                          }`}
                        >
                          {tierName}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <span className="text-red-600 font-medium">
                          ₦{outstandingBalance.toLocaleString()}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <span
                          className={`font-medium ${netBalance < 0 ? 'text-red-600' : 'text-green-600'}`}
                        >
                          {netBalance < 0 ? '-' : ''}₦{Math.abs(netBalance).toLocaleString()}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
              <div className="text-sm text-gray-500">
                Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} of {totalItems}{' '}
                customers
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="px-3 py-1 border border-gray-300 rounded-lg text-sm"
                >
                  <option value={10}>10 per page</option>
                  <option value={25}>25 per page</option>
                  <option value={50}>50 per page</option>
                  <option value={100}>100 per page</option>
                </select>
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-700">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      <AddDistributorModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSuccess={fetchDistributors}
      />
    </div>
  );
};

export default DistributorsPage;
